import { client } from '@/apollo/vue-apollo';
import axios from 'axios';
import { ActionsAddInvoiceCopyMailDocument, ActionsAddManualActionDocument, ActionsAddManualActionMutationVariables, ActionsGetSendInvoiceCopyMailDataDocument, Risolto_Action, Risolto_Action_Status_Enum, Risolto_Channel_Enum, ActionsUpdateActionMetadata, ActionsUpdateActionMetadataMutationVariables, ActionsUpdateChannelEndpointMutationVariables, ActionsUpdateChannelEndpoint, ActionsGetSendCreditNoteCopyMailDataDocument, ActionsAddManualActionCreditNoteCopyDocument, ActionsAddManualActionCreditNoteCopyMutationVariables, ActionsAddCreditNoteCopyMailMutationVariables, ActionsAddCreditNoteCopyMailDocument, ActionsGetSendCreditNoteCopyDataDocument } from '@/generated/graphql';
import { GET_SEND_INVOICE_COPY_DATA, INSERT_MANUAL_ACTION_INVOICE_COPY } from '@/gql';
import AttachmentHelper from '@/helpers/attachment';

export function actionAddManualAction(variables: ActionsAddManualActionMutationVariables) {
	return client.mutate({
		mutation: ActionsAddManualActionDocument,
		variables: variables
	});
}

export async function actionAddInvoiceCopyMail(
	recipient: string,
	invoices: string[]
) {
	const result = await client
		.query({
			query: ActionsGetSendInvoiceCopyMailDataDocument,
			variables: {
				'recipient': recipient,
				'invoices': invoices
			}
		});

	const recipientRecord = result.data.recipient;
	const toChannelEndpoint = recipientRecord.channel_endpoints.length > 0 ? recipientRecord.channel_endpoints[0] : undefined;
	const postalProvider = recipientRecord.legal_entity.communication_postal_provider ? recipientRecord.legal_entity.communication_postal_provider : recipientRecord.legal_entity.default_postal_provider;
	const communicationTemplates = recipientRecord.legal_entity.communication_templates;

	const vars = {
		participant: recipientRecord.participant,
		recipient: recipientRecord.reference,
		legalEntity: recipientRecord.legal_entity.reference,
		metadata: {
			'channels': [
				{
					'add_front_page': postalProvider.add_front_page,
					'to': {
						...toChannelEndpoint?.address,
						'contact_first_name': toChannelEndpoint?.contact ? toChannelEndpoint.contact.first_name : null,
						'contact_last_name': toChannelEndpoint?.contact ? toChannelEndpoint.contact.last_name : null,
						'recipient_name': recipientRecord?.name
					},
					'channel': 'MAIL',
					...(communicationTemplates.length > 0) ? {
						'template': {
							'locale': toChannelEndpoint?.locale ? toChannelEndpoint.locale : (recipientRecord.language_code ? recipientRecord.language_code : 'nl'),
							'channel': 'MAIL',
							'template_id': 'invoice_copy',
							'tone_of_voice': 'FRIENDLY'
						}
					} : null
				}
			],
			'attachments': recipientRecord.invoices.length > 0
				? recipientRecord.invoices[0].attachments.filter((attachment) => {
					return AttachmentHelper.isPdf(attachment);
				})
				: []
		},
		invoice: invoices[0],
		channelEndpoint: toChannelEndpoint?.reference,
		start_time: new Date()
	};

	return client.mutate({
		mutation: ActionsAddInvoiceCopyMailDocument,
		variables: vars
	});
}

export async function actionAddInvoiceCopyEmail(
	participant: string,
	legalEntity: string,
	recipient: string,
	invoice: string
) {
	const result = await client
		.query({
			query: GET_SEND_INVOICE_COPY_DATA,
			variables: {
				'participant': participant,
				'legalEntity': legalEntity,
				'recipient': recipient,
				'invoices': [invoice]
			}
		});

	const endpoint = (result.data.channel_endpoints && result.data.channel_endpoints.length > 0) ? result.data.channel_endpoints[0] : null;
	const legal_entity = result.data.legal_entity[0];
	const recipientRecord = result.data.recipient[0];
	const invoices = result.data.invoices;

	const vars = {
		participant: participant,
		recipient: recipient,
		legalEntity: legalEntity,
		metadata: {
			'channels': [
				{
					'to': endpoint?.channel_endpoint.endpoint,
					'_from': legal_entity.communication.email_from,
					'from_name': legal_entity.communication.email_from_name,
					'channel': 'EMAIL',
					'template': {
						'locale': endpoint?.channel_endpoint.locale ? endpoint.channel_endpoint.locale : (recipientRecord.language_code ? recipientRecord.language_code : 'nl'),
						'channel': 'EMAIL',
						'template_id': 'invoice_copy',
						'tone_of_voice': 'FRIENDLY'
					}
				}
			],
			'attachments': invoices.length > 0
				? invoices[0].attachments.filter((attachment) => {
					return AttachmentHelper.isPdf(attachment);
				})
				: []
		},
		invoice: invoice,
		channelEndpoint: endpoint?.channel_endpoint.reference,
		start_time: new Date()
	};

	return client.mutate({
		mutation: INSERT_MANUAL_ACTION_INVOICE_COPY,
		variables: vars
	});
}

export function actionAddInvoiceGroupCopyEmail(
	participant: string,
	legalEntity: string,
	recipient: string,
	invoices: string[]
) {
	const payload = {
		participant: participant,
		legal_entity: legalEntity,
		recipient: recipient,
		invoices: invoices
	};

	return axios.post(`https://${process.env.VUE_APP_API_ENDPOINT}/dunning/v1/create-invoice-group-copy-action`, payload, {
		headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
	});
}

export async function actionAddCreditNoteCopyMail(
	recipient: string,
	creditNotes: string[]
) {
	const result = await client
		.query({
			query: ActionsGetSendCreditNoteCopyMailDataDocument,
			variables: {
				'recipient': recipient,
				'creditNotes': creditNotes
			}
		});

	const recipientRecord = result.data.recipient;
	const toChannelEndpoint = recipientRecord.channel_endpoints.length > 0 ? recipientRecord.channel_endpoints[0] : undefined;
	const postalProvider = recipientRecord.legal_entity.communication_postal_provider ? recipientRecord.legal_entity.communication_postal_provider : recipientRecord.legal_entity.default_postal_provider;
	const communicationTemplates = recipientRecord.legal_entity.communication_templates;

	const vars: ActionsAddCreditNoteCopyMailMutationVariables = {
		participant: recipientRecord.participant,
		recipient: recipientRecord.reference,
		legalEntity: recipientRecord.legal_entity.reference,
		metadata: {
			'channels': [
				{
					'add_front_page': postalProvider.add_front_page,
					'to': {
						...toChannelEndpoint?.address,
						'contact_first_name': toChannelEndpoint?.contact ? toChannelEndpoint.contact.first_name : null,
						'contact_last_name': toChannelEndpoint?.contact ? toChannelEndpoint.contact.last_name : null,
						'recipient_name': recipientRecord?.name
					},
					'channel': 'MAIL',
					...(communicationTemplates.length > 0) ? {
						'template': {
							'locale': toChannelEndpoint?.locale ? toChannelEndpoint.locale : (recipientRecord.language_code ? recipientRecord.language_code : 'nl'),
							'channel': 'MAIL',
							'template_id': 'credit_note_copy',
							'tone_of_voice': 'FRIENDLY'
						}
					} : null
				}
			],
			'attachments': recipientRecord.credit_notes.length > 0 && recipientRecord.credit_notes[0].attachments
				? recipientRecord.credit_notes[0].attachments.filter((attachment) => {
					return AttachmentHelper.isPdf(attachment);
				})
				: []
		},
		creditNote: creditNotes[0],
		channelEndpoint: toChannelEndpoint?.reference,
		start_time: new Date()
	};

	return client.mutate({
		mutation: ActionsAddCreditNoteCopyMailDocument,
		variables: vars
	});
}

export async function actionAddCreditNoteCopyEmail(
	participant: string,
	legalEntity: string,
	recipient: string,
	creditNote: string
) {
	const result = await client
		.query({
			query: ActionsGetSendCreditNoteCopyDataDocument,
			variables: {
				'participant': participant,
				'legalEntity': legalEntity,
				'recipient': recipient,
				'creditNotes': [creditNote]
			}
		});

	const endpoint = (result.data.channel_endpoints && result.data.channel_endpoints.length > 0) ? result.data.channel_endpoints[0] : null;
	const legal_entity = result.data.legal_entity[0];
	const creditNotes = result.data.credit_notes;

	const vars: ActionsAddManualActionCreditNoteCopyMutationVariables = {
		participant: participant,
		recipient: recipient,
		legalEntity: legalEntity,
		metadata: {
			'channels': [
				{
					'to': endpoint?.channel_endpoint.endpoint,
					'_from': legal_entity.communication.email_from,
					'from_name': legal_entity.communication.email_from_name,
					'channel': 'EMAIL',
					'template': {
						'locale': endpoint?.channel_endpoint.locale ? endpoint.channel_endpoint.locale : 'nl_BE',
						'channel': 'EMAIL',
						'template_id': 'credit_note_copy',
						'tone_of_voice': 'FRIENDLY'
					}
				}
			],
			'attachments': creditNotes.length > 0 && creditNotes[0].attachments
				? creditNotes[0].attachments.filter((attachment) => {
					return AttachmentHelper.isPdf(attachment);
				})
				: []
		},
		creditNote: creditNote,
		channelEndpoint: endpoint?.channel_endpoint.reference,
		start_time: new Date()
	};

	return client.mutate({
		mutation: ActionsAddManualActionCreditNoteCopyDocument,
		variables: vars
	});
}

export function actionForceOpen(
	participant: string,
	actions: [Risolto_Action]
) {
	// Filter actions
	let filteredActions = actions.filter((action) => {
		return action.status === Risolto_Action_Status_Enum.Canceled || action.status === Risolto_Action_Status_Enum.Draft;
	});

	const payload = {
		participant: participant,
		actions: filteredActions.map((action) => { return action.reference; })
	};

	return axios.post(`https://${process.env.VUE_APP_API_ENDPOINT}/dunning/v1/action/force-open`, payload, {
		headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
	});
}

export function actionForceCancel(
	participant: string,
	actions: [Risolto_Action]
) {
	// Filter actions
	let filteredActions = actions.filter((action) => {
		return action.status === Risolto_Action_Status_Enum.Draft ||
			action.status === Risolto_Action_Status_Enum.Open;
	});

	const payload = {
		participant: participant,
		actions: filteredActions.map((action) => { return action.reference; })
	};

	return axios.post(`https://${process.env.VUE_APP_API_ENDPOINT}/dunning/v1/action/force-cancel`, payload, {
		headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
	});
}

export function actionUpdateMetadata(
	action: string,
	metadata: object,
	channel: Risolto_Channel_Enum
) {
	const variables: ActionsUpdateActionMetadataMutationVariables = {
		action,
		metadata,
		channel
	};

	return client.mutate({
		mutation: ActionsUpdateActionMetadata,
		variables: variables
	});
}

export function actionUpdateChannelEndpoint(
	action: string,
	channelEndpoint: string
) {
	const variables: ActionsUpdateChannelEndpointMutationVariables = {
		action,
		channelEndpoint
	};

	return client.mutate({
		mutation: ActionsUpdateChannelEndpoint,
		variables: variables
	});
}

export function actionUpdateDunningNotice(
	participant: string,
	legalEntity: string,
	recipient: string,
	action: string,
	invoices: { reference: string, attachment: boolean }[],
	creditNotes: { reference: string, attachment: boolean }[],
	debitNotes: { reference: string, attachment: boolean }[],
	payments: { reference: string, attachment: boolean }[],
	reminder_costs: number | null,
	interest: number | null,
	enabledDamageClause: boolean | null,
	enabledReminderCosts: boolean | null,
	overrideReminderCosts: number | null,
	actionSource: string
) {
	const payload: Record<string, any> = {
		participant: participant,
		legal_entity: legalEntity,
		recipient: recipient,
		action: action,
		invoices: invoices,
		credit_notes: creditNotes,
		debit_notes: debitNotes,
		payments: payments,
		reminder_costs: reminder_costs,
		interest: interest,
		enabled_damage_clauses: enabledDamageClause,
		enabled_reminder_costs: enabledReminderCosts,
		override_reminder_costs: overrideReminderCosts,
		source: actionSource
	};

	return axios.post(`https://${process.env.VUE_APP_API_ENDPOINT}/action/update-dunning-notice`, payload, {
		headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
	});
}
